import { useNavigate, useParams } from 'react-router-dom';
import { Users } from 'lucide-react';
import { useWaitingRoom } from '@core/session/useWaitingRoom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { PageHeader, PILL_PRIMARY, PILL_QUIET } from '@/components/learning/ui';
import { useLearnText } from '@/lib/learnText';

/**
 * Waiting for a turn.
 *
 * Every rule here — admission arriving on the session document, the latch that
 * stops a rewrite re-entering the call, dropped meaning reaped rather than
 * merely silent — lives in the shared useWaitingRoom hook, which the mobile
 * screen also uses. This page only draws.
 *
 * Redesign 2026-10 (board v1), on the web: the place in line large on the hero
 * card, and leaving the queue as the quiet action under it. The phone's
 * «تدرّب وأنت تنتظر» is left out on purpose: here, leaving this page leaves
 * the queue (the hook's unmount), so a practice link would cost the child
 * their place.
 */
export default function WaitingRoomPage() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const { identity } = useAuth();
  const { t } = useLanguage();
  const tl = useLearnText();

  const { position, dropped, rejoin, leave } = useWaitingRoom({
    sessionId,
    studentId: identity?.userId ?? '',
    studentName: identity?.displayName ?? '',
    onAdmitted: () => navigate(`/call/${sessionId}`),
  });

  const goHome = () => {
    leave();
    navigate('/home');
  };

  const leaveButton = (
    <button type="button" data-testid="leave-queue" onClick={goHome} className={PILL_QUIET}>
      {t('student.leaveQueue')}
    </button>
  );

  if (!sessionId) {
    // A route reached without a session must say so rather than sit on an
    // empty queue forever.
    return (
      <div className="card-soft mx-auto flex max-w-md flex-col items-center gap-4 p-8 text-center">
        <p data-testid="no-session" role="alert" className="text-danger">{t('student.sessionNotResolved')}</p>
        {leaveButton}
      </div>
    );
  }

  return (
    <div className="mx-auto flex w-full max-w-xl flex-col items-center gap-6">
      <div className="w-full">
        <PageHeader title={t('student.waitingRoom')} />
      </div>

      {dropped ? (
        <section className="card-soft flex w-full flex-col items-center gap-4 p-10 text-center">
          {/* Being dropped is not the student's doing — a backgrounded tab
              stops beating and ages out — so it reads as a state with a way
              back, not as an error. */}
          <span className="flex h-16 w-16 items-center justify-center rounded-full bg-lime text-accent-foreground">
            <Users className="h-7 w-7" aria-hidden />
          </span>
          <p data-testid="dropped" className="text-lg font-semibold text-ink">{t('student.removedFromQueue')}</p>
          <button type="button" data-testid="rejoin" onClick={rejoin} className={PILL_PRIMARY}>
            {t('student.rejoinQueue')}
          </button>
        </section>
      ) : (
        <section className="hero-card flex w-full flex-col items-center gap-2 p-12 text-center">
          <p className="text-sm opacity-90">{tl('waiting.queueLabel')}</p>
          <p data-testid="queue-position" className="text-7xl font-semibold text-lime" dir="ltr">
            {position === null
              ? <span className="text-2xl">{t('student.joining')}</span>
              : new Intl.NumberFormat('en-US').format(position)}
          </p>
        </section>
      )}

      {leaveButton}
    </div>
  );
}
